import React from "react";

interface TextSegment {
    text: string;
    isMatch: boolean;
    start: number;
}

export function splitOnMatches(text: string, query: string): TextSegment[] {
    if (!text) return [];
    if (!query) return [{ text, isMatch: false, start: 0 }];

    const haystack = text.toLowerCase();
    const needle = query.toLowerCase();
    const segments: TextSegment[] = [];

    let cursor = 0;
    let matchIndex = haystack.indexOf(needle, cursor);

    while (matchIndex !== -1) {
        if (matchIndex > cursor) {
            segments.push({
                text: text.slice(cursor, matchIndex),
                isMatch: false,
                start: cursor,
            });
        }
        segments.push({
            text: text.slice(matchIndex, matchIndex + needle.length),
            isMatch: true,
            start: matchIndex,
        });

        cursor = matchIndex + needle.length;
        matchIndex = haystack.indexOf(needle, cursor);
    }

    if (cursor < text.length) {
        segments.push({
            text: text.slice(cursor),
            isMatch: false,
            start: cursor,
        });
    }

    return segments;
}

/**
 * Renders text with every occurrence of query highlighted, using the same
 * classes Convo Details highlights with. Built from React nodes rather than an
 * HTML string so transcript text is never interpolated into markup.
 */
export function HighlightedText({
    text,
    query,
    matchRef,
    onlyMatchStart,
}: {
    text: string;
    query: string;
    matchRef?: (start: number, element: HTMLSpanElement | null) => void;
    onlyMatchStart?: number;
}) {
    // A clipped snippet already supplies the exact occurrence. Searching it
    // again could choose a different offset within repeated text.
    const segments: TextSegment[] =
        onlyMatchStart === undefined
            ? splitOnMatches(text, query)
            : [
                  {
                      text: text.slice(0, onlyMatchStart),
                      isMatch: false,
                      start: 0,
                  },
                  {
                      text: text.slice(
                          onlyMatchStart,
                          onlyMatchStart + query.length,
                      ),
                      isMatch: true,
                      start: onlyMatchStart,
                  },
                  {
                      text: text.slice(onlyMatchStart + query.length),
                      isMatch: false,
                      start: onlyMatchStart + query.length,
                  },
              ];

    return (
        <React.Fragment>
            {segments.map((segment, index) =>
                segment.isMatch ? (
                    <span
                        key={index}
                        className="transcriptionHighlight selectionColors"
                        ref={
                            matchRef
                                ? (element) => matchRef(segment.start, element)
                                : undefined
                        }
                    >
                        {segment.text}
                    </span>
                ) : (
                    <React.Fragment key={index}>{segment.text}</React.Fragment>
                ),
            )}
        </React.Fragment>
    );
}
